import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getTableAndTicketsAvailable, getTablesAndTicketsAvailableSelector} from '@/store/organization-tables';
//mui
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from '@mui/material';
//constants
import {
	DOLLAR,
	DOWNLOAD_CSV,
	PRICE,
	RIGHT,
	PAY_STATUS,
	TYPE_OF_SEAT,
	TABLE_PURCHASER,
	defaultPage,
	defaultPageSize,
	PURCHASER,
	CONTACT_PHONE,
} from '@/utils/constants';
//services
import {OrganizationTableAndTicketsService} from '@/utils/services';
//helpers
import {downloadFile, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as ImportFileIcon} from '@/assets/icons/import-file.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
//enums
import {STATISTIC, TABLE_STATUS} from '@/utils/enums';
//components
import Loader from '@/shared/components/Loader';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
//styles
import {statisticAvailableTicketsStyle} from './StatisticAvailableTickets.styles';

const StatisticAvailableTickets = () => {
	const dispatch = useAppDispatch();

	const [currentPage, setCurrentPage] = useState(defaultPage);
	const [isDataLoaded, setIsDataLoaded] = useState(false);
	const [searchName, setSearchName] = useState('');

	const [sortColumn, setSortColumn] = useState<string>();
	const [isOrderDesc, setIsOrderDesc] = useState<boolean>();

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const availableTickets = useAppSelector(getTablesAndTicketsAvailableSelector);

	const {data, pageSize, totalCount} = availableTickets;

	console.log('currentEvent', currentEvent)

	useEffect(() => {
		if (currentEvent) getTicketsAvailable(searchName);
	}, [currentEvent, currentPage, sortColumn, isOrderDesc]);

	const handleSortChange = (newSortColumn: string) => () => {
		const isAsc = sortColumn !== newSortColumn || !isOrderDesc;
		setIsOrderDesc(isAsc);
		setSortColumn(newSortColumn);
	};

	const getTicketsAvailable = async (value?: string) => {
		setIsDataLoaded(false);

		try {
			const reqBody = {
				eventId: currentEvent!.id,
				page: currentPage,
				sortColumn,
				isOrderDesc,
				searchName: value,
			};

			await dispatch(getTableAndTicketsAvailable(reqBody)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsDataLoaded(true);
		}
	};

	const handleDownloadSvc = async () => {
		if (currentEvent) {
			const file = await OrganizationTableAndTicketsService.getTableAndTicketsAvailableCsv(currentEvent.id);
			downloadFile(file.data, STATISTIC.AVAILABLE_TICKETS);
		}
	};

	const handleSearchChange = (value: string) => {
		setSearchName(value);
		getTicketsAvailable(value);
	};

	const handlePaginationChange = (step: number) => {
		if (isOrderDesc) setIsOrderDesc(false);
		if (sortColumn?.length) setSortColumn('');

		setCurrentPage(step);
	};

	const handlePressEnter = () => {
		setCurrentPage(defaultPage);
		if (currentEvent) getTicketsAvailable();
	};

	const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

	return (
		<Box sx={statisticAvailableTicketsStyle.wrapper}>
			<SearchHeader
				page={currentPage}
				items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
				total={totalCount}
				onPressEnter={handlePressEnter}
				onSearchChange={handleSearchChange}
				actionButton={
					<Button
						onClick={handleDownloadSvc}
						title={DOWNLOAD_CSV}
						disabled={!totalCount}
						startIcon={<ImportFileIcon />}
						sx={statisticAvailableTicketsStyle.searchButton}
					/>
				}
			/>

			{isDataLoaded ? (
				totalCount ? (
					<Box sx={statisticAvailableTicketsStyle.tableContent}>
						<TableContainer sx={statisticAvailableTicketsStyle.container}>
							<Table sx={statisticAvailableTicketsStyle.table}>
								<TableHead>
									<TableRow sx={statisticAvailableTicketsStyle.tableHeader}>
										<TableCell>{TYPE_OF_SEAT}</TableCell>

										<TableCell>
											<TableSortLabel active onClick={handleSortChange(PURCHASER)} IconComponent={UpDownIcon}>
												{TABLE_PURCHASER}
											</TableSortLabel>
										</TableCell>

										<TableCell>{CONTACT_PHONE}</TableCell>

										<TableCell align={RIGHT}>
											<TableSortLabel active onClick={handleSortChange(PRICE)} IconComponent={UpDownIcon}>
												{PRICE}
											</TableSortLabel>
										</TableCell>

										<TableCell>{PAY_STATUS}</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{data.map((row) => (
										<TableRow key={row.id} sx={statisticAvailableTicketsStyle.tableBody}>
											<TableCell>{row.typeOfSeat}</TableCell>

											<TableCell>{row.purchaser || '-'}</TableCell>

											<TableCell>{row.contactPhone || '-'}</TableCell>

											<TableCell align={RIGHT}>
												{DOLLAR}
												{row.price}
											</TableCell>

											<TableCell align={RIGHT}>
												<Typography sx={getPayStatusSx(row.isPaid ? TABLE_STATUS.PAID : TABLE_STATUS.PENDING)}>
													{row.isPaid ? TABLE_STATUS.PAID : TABLE_STATUS.PENDING}
													<EllipseIcon />
												</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<Box sx={statisticAvailableTicketsStyle.pagination}>
							<Pagination
								page={currentPage}
								pageSize={pageSize}
								total={totalCount}
								onChange={(step) => handlePaginationChange(step)}
							/>
						</Box>
					</Box>
				) : (
					<EmptyTableData />
				)
			) : (
				<Loader parentClassName={statisticAvailableTicketsStyle.loader} size={50} />
			)}
		</Box>
	);
};

export default StatisticAvailableTickets;
